// Prompt AC CAMBIO 2 (2026-09-21): lista de notas compartida por la ficha de
// trabajo y el permiso — mas reciente arriba, cada una con fecha y autor
// debajo en chico (mismo patron que ya tenia JobDetailPage, extraido aqui
// para reusarlo en PermitsPage sin duplicar ~80 lineas).
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Save as SaveIcon, StickyNote, Search } from 'lucide-react';

// Prompt AD (2026-09-21) — Office Workflow Notes tab: tipo de nota en
// pastilla (General/Customer/Internal/Follow Up/System) + buscador + filtro
// por tipo. `showType=false` (default) mantiene el comportamiento simple
// que ya usan Jobs/Permits.
const NOTE_TYPES = [
  { value: 'general', label: 'General', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  { value: 'customer', label: 'Customer', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'internal', label: 'Internal', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'follow_up', label: 'Follow Up', className: 'bg-violet-50 text-violet-700 border-violet-200' },
  { value: 'system', label: 'System', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
];
const noteTypeMeta = (v) => NOTE_TYPES.find(t => t.value === v) || NOTE_TYPES[0];

export default function NotesLogSection({ notesLog = [], onAddNote, saving, testIdPrefix = 'notes-log', extraBlock = null, showType = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftType, setDraftType] = useState('general');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const handleSave = async () => {
    const text = draft.trim();
    if (!text) { setEditing(false); return; }
    await onAddNote(text, draftType);
    setDraft('');
    setDraftType('general');
    setEditing(false);
  };

  const visible = [...notesLog]
    .filter(n => typeFilter === 'all' || (n.note_type || 'general') === typeFilter)
    .filter(n => !search.trim() || (n.text || '').toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));

  return (
    <Card className="bg-white border-gray-100" data-testid={`${testIdPrefix}-card`}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <StickyNote className="w-4 h-4 text-amber-500" />
            <span className="text-xs uppercase tracking-wide text-gray-400 font-semibold">Notes</span>
          </div>
          {!editing && (
            <Button variant="ghost" size="sm" onClick={() => { setDraft(''); setEditing(true); }} className="h-7 text-xs text-[#003366]" data-testid={`${testIdPrefix}-add-btn`}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Note
            </Button>
          )}
        </div>

        {extraBlock}

        {showType && !editing && notesLog.length > 0 && (
          <div className="flex items-center gap-2 mb-2 flex-wrap" data-testid={`${testIdPrefix}-filters`}>
            <div className="relative flex-1 min-w-[140px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search notes…" className="h-8 pl-7 text-xs" data-testid={`${testIdPrefix}-search`} />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-8 w-32 text-xs" data-testid={`${testIdPrefix}-type-filter`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {NOTE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        {editing ? (
          <div className="space-y-2">
            {showType && (
              <Select value={draftType} onValueChange={setDraftType}>
                <SelectTrigger className="h-8 w-40 text-xs" data-testid={`${testIdPrefix}-type-select`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {NOTE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Textarea value={draft} onChange={e => setDraft(e.target.value)} rows={4} className="text-sm" data-testid={`${testIdPrefix}-textarea`} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => { setDraft(''); setEditing(false); }} disabled={saving}>Cancel</Button>
              <Button size="sm" className="bg-[#003366] hover:bg-[#004080] text-white" onClick={handleSave} disabled={saving} data-testid={`${testIdPrefix}-save-btn`}>
                <SaveIcon className="w-3.5 h-3.5 mr-1" /> Save
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2" data-testid={`${testIdPrefix}-list`}>
            {visible.length === 0 ? (
              <p className="text-sm text-gray-400 italic" data-testid={`${testIdPrefix}-empty`}>No notes yet.</p>
            ) : (
              visible.map((entry, i) => (
                <div key={entry.id || i} className="border-l-2 border-amber-300 pl-2.5 py-0.5" data-testid={`${testIdPrefix}-entry-${i}`}>
                  {showType && (
                    <span className={`inline-block text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded border mb-0.5 ${noteTypeMeta(entry.note_type).className}`} data-testid={`${testIdPrefix}-entry-type-${i}`}>
                      {noteTypeMeta(entry.note_type).label}
                    </span>
                  )}
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{entry.text}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5" data-testid={`${testIdPrefix}-entry-meta-${i}`}>
                    {(entry.created_at || '').slice(0, 10)}{entry.author_name ? ` · ${entry.author_name}` : ''}
                  </p>
                </div>
              ))
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
